import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Info, Loader2, Pencil, Plus } from 'lucide-react';
import { toast } from 'sonner';

import { api, ErroApi } from '@/lib/api';
import { paraBooleano } from '@/lib/utils';
import type { RespostaPaginada } from '@/components/paginacao';
import type { Especialidade, Profissional } from '@/features/admin/tipos';
import { CabecalhoPagina } from '@/components/cabecalho-pagina';
import { BarraBusca } from '@/components/barra-busca';
import { Paginacao } from '@/components/paginacao';
import { Botao } from '@/components/ui/button';
import { Entrada, Selecao } from '@/components/ui/input';
import { Campo } from '@/components/ui/campo';
import { Etiqueta } from '@/components/ui/badge';
import {
  Tabela,
  TabelaCabecalho,
  TabelaCelula,
  TabelaCorpo,
  TabelaLinha,
  TabelaTitulo,
  TabelaVazia,
} from '@/components/ui/table';
import { EsqueletoTabela } from '@/components/ui/skeleton';
import {
  Dialogo,
  DialogoCabecalho,
  DialogoConteudo,
  DialogoCorpo,
  DialogoDescricao,
  DialogoRodape,
  DialogoTitulo,
} from '@/components/ui/dialog';

const esquema = z.object({
  nome: z.string().min(3, 'Informe o nome do profissional.'),
  conselho: z.string().optional(),
  especialidadeId: z.string().optional(),
  ativo: z.boolean().optional(),
});

type Formulario = z.infer<typeof esquema>;

const VAZIO: Formulario = { nome: '', conselho: '', especialidadeId: '' };

const COLUNAS = 6;

/**
 * Cadastro dos profissionais executantes.
 *
 * A especialidade é opcional: serve para distinguir homônimos na lista do
 * agendamento manual, não para restringir a escolha do operador.
 */
export function PaginaProfissionais() {
  const queryClient = useQueryClient();
  const [pagina, setPagina] = React.useState(1);
  const [busca, setBusca] = React.useState('');
  const [emEdicao, setEmEdicao] = React.useState<Profissional | null>(null);
  const [dialogoAberto, setDialogoAberto] = React.useState(false);

  const parametros = new URLSearchParams({ pagina: String(pagina), limite: '20' });
  if (busca) parametros.set('busca', busca);

  const { data, isPending } = useQuery({
    queryKey: ['profissionais', pagina, busca],
    queryFn: () =>
      api.get<RespostaPaginada<Profissional>>(`/profissionais?${parametros.toString()}`),
  });

  const { data: especialidades } = useQuery({
    queryKey: ['especialidades-ativas'],
    queryFn: () => api.get<Pick<Especialidade, 'id' | 'nome'>[]>('/especialidades/ativas'),
    staleTime: 5 * 60_000,
  });

  const formulario = useForm<Formulario>({
    resolver: zodResolver(esquema),
    defaultValues: VAZIO,
  });

  const abrirCriacao = () => {
    setEmEdicao(null);
    formulario.reset(VAZIO);
    setDialogoAberto(true);
  };

  const abrirEdicao = (profissional: Profissional) => {
    setEmEdicao(profissional);
    formulario.reset({
      nome: profissional.nome,
      conselho: profissional.conselho ?? '',
      especialidadeId: profissional.especialidadeId ?? '',
      ativo: profissional.ativo,
    });
    setDialogoAberto(true);
  };

  const salvar = useMutation({
    mutationFn: (dados: Formulario) => {
      const corpo = {
        ...dados,
        conselho: dados.conselho || undefined,
        especialidadeId: dados.especialidadeId || undefined,
      };

      return emEdicao
        ? api.patch<Profissional>(`/profissionais/${emEdicao.id}`, corpo)
        : api.post<Profissional>('/profissionais', { ...corpo, ativo: undefined });
    },
    onSuccess: async () => {
      toast.success(emEdicao ? 'Profissional atualizado.' : 'Profissional cadastrado.');
      setDialogoAberto(false);
      await queryClient.invalidateQueries({ queryKey: ['profissionais'] });
    },
    onError: (erro) => {
      toast.error(erro instanceof ErroApi ? erro.mensagemUsuario : 'Não foi possível salvar.');
    },
  });

  const aoEnviar = formulario.handleSubmit((dados) => salvar.mutate(dados));

  return (
    <>
      <CabecalhoPagina
        titulo="Profissionais"
        descricao="Profissionais executantes oferecidos à Regulação no agendamento preenchido à mão. O quadro pode ser importado do CNES pela tela de Locais de atendimento."
        acoes={
          <Botao onClick={abrirCriacao}>
            <Plus aria-hidden="true" />
            Novo profissional
          </Botao>
        }
      />

      <div className="mb-3">
        <BarraBusca
          valor={busca}
          aoMudar={(valor) => {
            setBusca(valor);
            setPagina(1);
          }}
          placeholder="Buscar por nome ou conselho"
        />
      </div>

      <Tabela>
        <TabelaCabecalho>
          <TabelaLinha>
            <TabelaTitulo>Nome</TabelaTitulo>
            <TabelaTitulo>Conselho</TabelaTitulo>
            <TabelaTitulo>Especialidade</TabelaTitulo>
            <TabelaTitulo>Origem</TabelaTitulo>
            <TabelaTitulo>Situação</TabelaTitulo>
            <TabelaTitulo className="text-right">Ações</TabelaTitulo>
          </TabelaLinha>
        </TabelaCabecalho>
        <TabelaCorpo>
          {isPending && <EsqueletoTabela colunas={COLUNAS} />}

          {!isPending && data?.dados.length === 0 && (
            <TabelaVazia colunas={COLUNAS}>Nenhum profissional cadastrado.</TabelaVazia>
          )}

          {data?.dados.map((profissional) => (
            <TabelaLinha key={profissional.id}>
              <TabelaCelula rotulo="Nome" className="font-medium">
                {profissional.nome}
                {profissional.cboDescricao && (
                  <span className="text-silenciado-texto block text-xs font-normal">
                    {profissional.cboDescricao}
                  </span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Conselho" className="text-silenciado-texto">
                {profissional.conselho ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Especialidade" className="text-silenciado-texto">
                {profissional.especialidade?.nome ?? '—'}
              </TabelaCelula>
              <TabelaCelula rotulo="Origem">
                {profissional.cnesOrigem ? (
                  <Etiqueta tom="marca" title={`Importado do CNES ${profissional.cnesOrigem}`}>
                    CNES {profissional.cnesOrigem}
                  </Etiqueta>
                ) : (
                  <span className="text-silenciado-texto text-xs">Cadastro manual</span>
                )}
              </TabelaCelula>
              <TabelaCelula rotulo="Situação">
                <Etiqueta tom={profissional.ativo ? 'sucesso' : 'neutro'}>
                  {profissional.ativo ? 'Ativo' : 'Inativo'}
                </Etiqueta>
              </TabelaCelula>
              <TabelaCelula className="text-right">
                <Botao
                  variante="fantasma"
                  tamanho="sm"
                  onClick={() => abrirEdicao(profissional)}
                  aria-label={`Editar ${profissional.nome}`}
                >
                  <Pencil aria-hidden="true" />
                  Editar
                </Botao>
              </TabelaCelula>
            </TabelaLinha>
          ))}
        </TabelaCorpo>
      </Tabela>

      {data && <Paginacao meta={data.meta} aoMudarPagina={setPagina} />}

      <Dialogo open={dialogoAberto} onOpenChange={setDialogoAberto}>
        <DialogoConteudo>
          <DialogoCabecalho>
            <DialogoTitulo>{emEdicao ? 'Editar profissional' : 'Novo profissional'}</DialogoTitulo>
            <DialogoDescricao>
              Dois profissionais com o mesmo nome só são aceitos quando o conselho os diferencia.
            </DialogoDescricao>

            {emEdicao?.cnesOrigem && (
              <p className="text-silenciado-texto flex items-start gap-1.5 text-xs">
                <Info className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
                Este cadastro veio do CNES {emEdicao.cnesOrigem}. Nome e ocupação são reescritos na
                próxima importação; conselho e especialidade, não — esses o CNES não devolve.
              </p>
            )}
          </DialogoCabecalho>

          <form onSubmit={(evento) => void aoEnviar(evento)} noValidate>
            <DialogoCorpo>
              <Campo
                id="prof-nome"
                rotulo="Nome"
                obrigatorio
                erro={formulario.formState.errors.nome?.message}
              >
                <Entrada autoFocus {...formulario.register('nome')} />
              </Campo>

              <Campo
                id="prof-conselho"
                rotulo="Conselho"
                ajuda="Registro de classe, ex.: CRM 12345/PB."
              >
                <Entrada {...formulario.register('conselho')} />
              </Campo>

              <Campo id="prof-especialidade" rotulo="Especialidade">
                <Selecao {...formulario.register('especialidadeId')}>
                  <option value="">Não informada</option>
                  {especialidades?.map((especialidade) => (
                    <option key={especialidade.id} value={especialidade.id}>
                      {especialidade.nome}
                    </option>
                  ))}
                </Selecao>
              </Campo>

              {emEdicao && (
                <Campo
                  id="prof-ativo"
                  rotulo="Situação"
                  ajuda="Inativar não afeta os agendamentos já registrados."
                >
                  <Selecao {...formulario.register('ativo', { setValueAs: paraBooleano })}>
                    <option value="true">Ativo</option>
                    <option value="false">Inativo</option>
                  </Selecao>
                </Campo>
              )}
            </DialogoCorpo>

            <DialogoRodape>
              <Botao variante="contorno" onClick={() => setDialogoAberto(false)}>
                Cancelar
              </Botao>
              <Botao type="submit" disabled={salvar.isPending}>
                {salvar.isPending && <Loader2 className="animate-spin" aria-hidden="true" />}
                Salvar
              </Botao>
            </DialogoRodape>
          </form>
        </DialogoConteudo>
      </Dialogo>
    </>
  );
}
